import { useEffect } from 'react';
import { useRouter } from '@tanstack/react-router';
import { useActivityStore } from '../../store/useActivityStore';
import { logDebug, logError } from '../../services/loggerService';

/**
 * Renderless bridge between pushService and the router. pushService runs
 * outside the router tree, so a tap on an OS notification stashes the
 * payload's `targetUrl` in useActivityStore; this consumes it and navigates.
 *
 * Same-origin targets go through router history, so a `?s=<secret>` on the
 * URL still reaches SecretLoginHandler. Other origins open in a new tab.
 */
export function PushTapNavigator() {
  const router = useRouter();
  const pendingPushRoute = useActivityStore((s) => s.pendingPushRoute);
  const setPendingPushRoute = useActivityStore((s) => s.setPendingPushRoute);

  useEffect(() => {
    if (!pendingPushRoute) return;
    const url = pendingPushRoute;
    setPendingPushRoute(null);

    if (url.startsWith('/')) {
      logDebug('PushTapNavigator: same-origin relative navigation', undefined, { target: url });
      router.history.push(url);
      return;
    }
    try {
      const parsed = new URL(url);
      if (parsed.origin === window.location.origin) {
        router.history.push(parsed.pathname + parsed.search + parsed.hash);
      } else {
        window.open(url, '_blank', 'noopener');
      }
    } catch (err) {
      logError(err instanceof Error ? err : new Error(String(err)), 'PushTapNavigator: invalid targetUrl', undefined, {
        url,
      });
    }
  }, [pendingPushRoute, router, setPendingPushRoute]);

  return null;
}
